import type { ComponentProps, ReactNode } from "react"
import { Flex, Popconfirm, Typography } from "antd"
import classNames from "classnames"
import { CloseOutlined, FileOutlined } from "@ant-design/icons"
import { AudioPreview } from "~/components/AudioPreview"
import { ImageThumbnail } from "~/components/ImageThumbnail"
import {
  defaultPreviewFrameSize,
  getPreviewFrameSizeClassName,
  previewFrameClassName,
  type PreviewFrameSize,
} from "./previewFrame"
import { VideoPreview } from "~/components/VideoPreview"
import styles from "./index.css"

const { Text } = Typography

const labelWidthBySize: Record<PreviewFrameSize, number> = {
  large: 128,
  medium: 96,
  small: 52,
}

type BaseResourcePreviewProps = {
  src: string
  className?: string
  label?: string
  onRemove?: () => void
  size?: PreviewFrameSize
}

type ImageResourcePreviewProps = BaseResourcePreviewProps & {
  type: "image"
  alt?: string
  preview?: ComponentProps<typeof ImageThumbnail>["preview"]
}

type VideoResourcePreviewProps = BaseResourcePreviewProps & {
  type: "video"
  alt?: string
  fit?: "contain" | "cover"
  muted?: boolean
  startTime?: number
  endTime?: number
  onMetadataLoaded?: (metadata: { height: number; width: number }) => void
  onPreviewOpen?: () => void
  poster?: string
  preview?: boolean
  width?: number | string
}

type AudioResourcePreviewProps = BaseResourcePreviewProps & {
  type: "audio"
  fingerprint?: string
}

type FileResourcePreviewProps = {
  type: "file"
  className?: string
  label?: string
  onRemove?: () => void
  size?: PreviewFrameSize
}

export type ResourcePreviewProps =
  | AudioResourcePreviewProps
  | FileResourcePreviewProps
  | ImageResourcePreviewProps
  | VideoResourcePreviewProps

const renderRemoveButton = (onRemove?: () => void) => {
  if (!onRemove) return null

  return (
    <Popconfirm
      cancelText="取消"
      okButtonProps={{ danger: true }}
      okText="移除"
      placement="top"
      title="确认移除该资源吗？"
      onConfirm={(event) => {
        event?.stopPropagation()
        onRemove()
      }}
      onCancel={(event) => {
        event?.stopPropagation()
      }}
    >
      <button
        aria-label="移除资源"
        className={styles.removeButton}
        type="button"
        onClick={(event) => event.stopPropagation()}
      >
        <CloseOutlined />
      </button>
    </Popconfirm>
  )
}

const renderPreviewLabel = (label?: string) => {
  if (!label) return null

  return (
    <Text
      className={styles.label}
      ellipsis={{ tooltip: label }}
      type="secondary"
    >
      {label}
    </Text>
  )
}

const renderPreviewWrapper = ({
  className,
  content,
  label,
  onRemove,
  width,
}: {
  className?: string
  content: ReactNode
  label?: string
  onRemove?: () => void
  width?: number | string
}) => {
  if (!label && !onRemove) return content

  return (
    <Flex
      className={classNames(styles.wrapper, className)}
      gap={6}
      style={{ width }}
      vertical
    >
      {content}
      {renderPreviewLabel(label)}
      {renderRemoveButton(onRemove)}
    </Flex>
  )
}

export const ResourcePreview = (props: ResourcePreviewProps) => {
  switch (props.type) {
    case "image":
      return renderPreviewWrapper({
        className: props.className,
        content: (
          <ImageThumbnail
            alt={props.alt}
            className={props.className}
            preview={props.preview}
            size={props.size}
            src={props.src}
          />
        ),
        label: props.label,
        onRemove: props.onRemove,
        width: labelWidthBySize[props.size || defaultPreviewFrameSize],
      })
    case "video":
      return renderPreviewWrapper({
        className: props.className,
        content: (
          <VideoPreview
            alt={props.alt}
            className={props.className}
            fit={props.fit}
            muted={props.muted}
            startTime={props.startTime}
            endTime={props.endTime}
            onMetadataLoaded={props.onMetadataLoaded}
            onPreviewOpen={props.onPreviewOpen}
            poster={props.poster}
            preview={props.preview}
            size={props.size}
            src={props.src}
            width={props.width}
          />
        ),
        label: props.label,
        onRemove: props.onRemove,
        width:
          props.width ||
          labelWidthBySize[props.size || defaultPreviewFrameSize],
      })
    case "audio":
      return renderPreviewWrapper({
        className: props.className,
        content: (
          <AudioPreview
            className={props.className}
            fingerprint={props.fingerprint}
            label={props.label}
            size={props.size}
            src={props.src}
          />
        ),
        onRemove: props.onRemove,
        width: labelWidthBySize[props.size || defaultPreviewFrameSize],
      })
    default:
      return renderPreviewWrapper({
        className: props.className,
        content: (
          <Flex
            className={classNames(
              previewFrameClassName,
              getPreviewFrameSizeClassName(
                props.size || defaultPreviewFrameSize,
              ),
              styles.filePreview,
              props.className,
            )}
            align="center"
            justify="center"
            vertical
          >
            <FileOutlined className={styles.fileIcon} />
            <Text type="secondary">{props.label || "文件"}</Text>
          </Flex>
        ),
        onRemove: props.onRemove,
        width: labelWidthBySize[props.size || defaultPreviewFrameSize],
      })
  }
}
